/**
 * NeedsAttention - the one thing on the dashboard that asks you to DO
 * something.
 *
 * This used to be a single sentence at the bottom of the greeting card
 * ("3 tasks are overdue"), which is the right information in the wrong place
 * twice over: it was the only actionable line on the page and it was not
 * clickable, and it sat inside a decorative band that made the greeting the
 * tallest card on screen.
 *
 * It is a card of its own now, beside "At a glance", and every line is a way
 * IN: overdue work, work due today, the next thing on the calendar, and
 * anything the AI is still chewing through. Each row goes somewhere.
 *
 * OVERDUE TASKS are listed by name too, the three most overdue first, each
 * opening its task; when there are more, a last row opens Tasks filtered to
 * Overdue. The total is the backend's real count (`overdueCount`) -
 * `tasks` is the capped urgent list, so counting it said "8" whenever there
 * were more (R4-03).
 *
 * AI WORK YOU STARTED gets a row PER RESOURCE, not a count: the table you set
 * extracting, the review you kicked off, the matter analysis still running
 * (`aiInProgress`), and the ones that finished in the last 24h (`aiReady` -
 * a time window, because the platform records no "opened since"). A count
 * says something is happening; a named row is the way back in. Both come from
 * the backend's `ai_run` meter, already org-, owner- and access-scoped and
 * capped at five each.
 *
 * WHEN THERE IS NOTHING it says so, once, calmly. A dashboard that invents
 * urgency to avoid an empty state teaches people to ignore it.
 */

import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PiBellRingingLight,
  PiCalendarCheckLight,
  PiCheckCircleLight,
  PiClockCountdownLight,
  PiSealCheckLight,
  PiSpinnerGapLight,
  PiWarningCircleLight,
  PiWarningLight,
} from 'react-icons/pi';
import GlassCard from '../../shared/brand/GlassCard';
import Spinner from '../../shared/TailwindComponents/Spinner';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { formatRelativeTime } from '../../shared/feed/feedTime';
import {
  DashboardAiWorkItem,
  DashboardAiWorkType,
  DashboardCalendarEvent,
  DashboardProcessingItem,
  DashboardUrgentTask,
} from '../../../store/reducer/DashboardReducer';
import { FieldTone } from '../../shared/brand/Field';
import { parseServerDate } from '../../../utils/functions';

/** How many overdue tasks are listed by name before "See all". */
export const OVERDUE_LISTED = 3;

export interface NeedsAttentionProps {
  tasks?: DashboardUrgentTask[];
  /** The real number of overdue tasks (overview.tasks_overdue). */
  overdueCount?: number;
  events?: DashboardCalendarEvent[];
  processing?: DashboardProcessingItem[];
  /** AI runs the user started that are still running. */
  aiInProgress?: DashboardAiWorkItem[];
  /** AI runs the user started that finished in the last 24h. */
  aiReady?: DashboardAiWorkItem[];
}

/** The licensed section each AI work type opens into (as ProcessingItems). */
const AI_SECTION: Record<DashboardAiWorkType, string> = {
  matter: 'matters',
  table: 'tables',
  review: 'reviews',
  research: 'research',
  draft: 'drafting',
};

/** Fallback title when the resource has no name yet - its type, said plainly. */
const AI_KIND: Record<DashboardAiWorkType, string> = {
  matter: 'Matter',
  table: 'Table',
  review: 'Review',
  research: 'Research',
  draft: 'Draft',
};

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

interface CalloutRow {
  key: string;
  icon: React.ReactNode;
  label: string;
  tone: FieldTone;
  onClick: () => void;
  /** Muted trailing status, e.g. "Ready 5m ago". Already translated. */
  meta?: string;
  /** Tooltip for the whole row (absolute state). Already translated. */
  hint?: string;
}

/** Tone → the dot and the glyph. Status colour, never decoration. */
const TONE_TEXT: Record<FieldTone, string> = {
  default: 'text-fg-muted',
  info: 'text-info-600 dark:text-info-400',
  success: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400',
  danger: 'text-danger-600 dark:text-danger-400',
};

const NeedsAttention: React.FC<NeedsAttentionProps> = ({
  tasks = [],
  overdueCount,
  events = [],
  processing = [],
  aiInProgress = [],
  aiReady = [],
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { hasSectionAccess } = useLicenseGate();

  const rows = useMemo<CalloutRow[]>(() => {
    const now = new Date();
    const overdueTasks: DashboardUrgentTask[] = [];
    let dueToday = 0;
    tasks.forEach((task) => {
      if (!task.due_date) return;
      const due = parseServerDate(task.due_date);
      if (!due) return;
      if (isSameDay(due, now)) dueToday += 1;
      else if (due < now) overdueTasks.push(task);
    });

    const out: CalloutRow[] = [];

    // The urgent list comes due-date ascending, so these are the most overdue.
    const overdueListed = overdueTasks.filter((task) => !!task.uuid).slice(0, OVERDUE_LISTED);
    overdueListed.forEach((task) => {
      const meta = String(t('Due {{when}}', { when: formatRelativeTime(task.due_date || undefined, t) }));
      out.push({
        key: `overdue-${task.uuid}`,
        icon: <PiWarningCircleLight />,
        label: task.title || String(t('Untitled task')),
        meta,
        hint: task.matter_title ? `${task.matter_title} · ${meta}` : meta,
        tone: 'danger',
        onClick: () => history.push(`/tasks/${task.uuid}`),
      });
    });
    const overdueTotal = Math.max(overdueCount ?? 0, overdueTasks.length);
    if (overdueTotal > overdueListed.length) {
      out.push({
        key: 'overdue',
        icon: <PiWarningCircleLight />,
        label: String(t('See all {{n}} overdue tasks', { n: overdueTotal })),
        tone: 'danger',
        onClick: () => history.push('/tasks?filter=overdue'),
      });
    }
    if (dueToday > 0) {
      out.push({
        key: 'today',
        icon: <PiClockCountdownLight />,
        label: String(t('{{count}} tasks are due today', { count: dueToday })),
        tone: 'warning',
        onClick: () => history.push('/tasks'),
      });
    }

    const nextToday = events.find((event) => {
      if (!event.start_time) return false;
      const start = new Date(event.start_time);
      return !Number.isNaN(start.getTime()) && isSameDay(start, now) && start >= now;
    });
    if (nextToday) {
      out.push({
        key: 'next',
        icon: <PiCalendarCheckLight />,
        label: String(t('Next up: {{title}}', { title: nextToday.title })),
        tone: 'info',
        onClick: () => history.push('/calendar'),
      });
    }

    const allowed = (item: DashboardAiWorkItem) =>
      hasSectionAccess(AI_SECTION[item.type]) && !!item.link;
    const titleOf = (item: DashboardAiWorkItem) =>
      item.title || String(t(AI_KIND[item.type]));

    const running = aiInProgress.filter(allowed);
    running.forEach((item) => {
      const meta = item.timestamp
        ? String(t('Started {{when}}', { when: formatRelativeTime(item.timestamp, t) }))
        : String(t('Running'));
      out.push({
        key: `ai-running-${item.type}-${item.uuid}`,
        /*
          SPINNER, NOT THE LOGO LOADER - a deliberate choice. This IS an AI
          process, so CLAUDE.md §6.7 would allow `<BrandLoader size="inline">`,
          but this card is a column of 16px STATUS GLYPHS coloured by tone, and
          up to five drawing logos stacked in a quarter-width card is the brand
          mark turned into noise. The logo belongs to the place the AI work is
          actually shown (AIProcessing on the resource's own page); here the
          row only says "still going", which is the plain `<Spinner
          size="glyph">`, inheriting the row's info tone via `current`.
        */
        icon: <Spinner size="glyph" tone="current" ariaLabel={String(t('Running'))} />,
        label: titleOf(item),
        meta,
        hint: `${t(AI_KIND[item.type])} · ${t('Running')}`,
        tone: 'info',
        onClick: () => history.push(item.link),
      });
    });

    // The matter/review "still processing" count predates the per-resource
    // rows; anything already listed above is not counted twice.
    const listed = new Set(running.map((item) => `${item.type}:${item.uuid}`));
    const unlisted = processing.filter((item) => !listed.has(`${item.type}:${item.uuid}`));

    if (unlisted.length > 0) {
      out.push({
        key: 'processing',
        icon: <PiSpinnerGapLight />,
        label: String(t('{{count}} items are still processing', { count: unlisted.length })),
        tone: 'info',
        onClick: () => history.push('/matters'),
      });
    }

    aiReady.filter(allowed).forEach((item) => {
      const when = formatRelativeTime(item.timestamp || undefined, t);
      const state =
        item.status === 'failed'
          ? { icon: <PiWarningCircleLight />, tone: 'danger' as FieldTone, meta: String(t('Failed {{when}}', { when })) }
          : item.status === 'partial'
            ? { icon: <PiWarningLight />, tone: 'warning' as FieldTone, meta: String(t('Partly finished {{when}}', { when })) }
            : { icon: <PiSealCheckLight />, tone: 'success' as FieldTone, meta: String(t('Ready {{when}}', { when })) };
      out.push({
        key: `ai-ready-${item.type}-${item.uuid}`,
        icon: state.icon,
        label: titleOf(item),
        meta: state.meta,
        hint: `${t(AI_KIND[item.type])} · ${state.meta}`,
        tone: state.tone,
        onClick: () => history.push(item.link),
      });
    });

    return out;
  }, [tasks, overdueCount, events, processing, aiInProgress, aiReady, hasSectionAccess, t, history]);

  return (
    <GlassCard
      icon={<PiBellRingingLight />}
      title={t('Needs you')}
      padding="none"
      className="overflow-hidden"
      fill
      bodyClassName="flex min-h-0 flex-1 flex-col"
      data-testid="needs-attention"
    >
      {rows.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 px-5 py-4 text-center">
          <PiCheckCircleLight aria-hidden className="h-6 w-6 text-success-500" />
          <p className="text-sm text-fg-body">{t('Nothing overdue — a clear run at it.')}</p>
        </div>
      ) : (
        <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col divide-y divide-hairline-soft overflow-y-auto [&>*]:flex-shrink-0">
          {rows.map((row) => (
            <button
              key={row.key}
              type="button"
              data-testid={`needs-attention-${row.key}`}
              onClick={row.onClick}
              title={row.hint}
              className="flex items-center gap-3 px-5 py-3 text-start transition-colors duration-200 ease-slide hover:bg-surface-sunken/70 focus:bg-surface-sunken/70 focus:outline-none"
            >
              <span className={clsx('flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4', TONE_TEXT[row.tone])}>
                {row.icon}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-200">
                {row.label}
              </span>
              {row.meta && (
                <span className="flex-shrink-0 text-xs text-fg-muted">
                  {row.meta}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

export default NeedsAttention;
